import { useState, useEffect, useRef } from 'react';
import { Form, Image, Row, Col, Button, Card } from 'react-bootstrap';
import { validateURL} from './utils/validation';
import icImgPreview from './assets/img/ic-img.svg';
import icImgEdit from './assets/img/ic-img-dark.svg';
import icDelete from './assets/img/ic-delete-danger.svg';
import gptwBanner from './assets/img/gptw-banner.svg';
import icApproved from './assets/img/ic-check-circle.svg';
import icRejected from './assets/img/ic-close-circle.svg';
import icRLifecycleReview from './assets/img/ic-lyfecycle-review.svg';
import icUnderReview from './assets/img/ic-under-review.svg';
import icValidationError from './assets/img/ic-danger.svg';

const CompanyAddGeneral = ({setShowErrorMessage, setForceRerenderKey, forceRerenderKey, handleResetLogo, formData, handleChange, logo, errors, handleGeneralSubmit, isEditPage, handleDeleteImage }) => {
    const [isEditing, setIsEditing] = useState(!isEditPage); // If adding, fields start enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // Store initial values
    const [localErrors, setLocalErrors] = useState({}); // ✅ Renamed errors state to avoid conflict
    const fileInputRef = useRef(null);
    const [urlInput, setUrlInput] = useState(formData.url || '');
    const [logoPreview, setLogoPreview] = useState(logo);

    useEffect(() => {
        setUrlInput(formData.url || ''); // ✅ Ensure urlInput updates when formData changes
    }, [formData.url]); 

    useEffect(() => {
        setLogoPreview(logo?.trim?.() || '');
    }, [logo]);

    useEffect(() => {
        if (logo === 'fallback') {
            setLogoPreview(icImgPreview); // set actual image URL
        } else {
            setLogoPreview(logo?.trim?.() || '');
        }
    }, [logo]);

    const handleTitleChange = (e) => {
        const { value } = e.target;

        // Live validation
        if (!value.trim()) {
            setLocalErrors((prev) => ({ ...prev, title: 'Company name is required.' }));
        } else {
            setLocalErrors((prev) => ({ ...prev, title: '' }));
        }

        handleChange({ target: { name: 'title', value } }); // Update parent state
    };

    // Handle URL field change
    const handleURLChange = (e) => {
        const { value } = e.target;
        setUrlInput(value);

        clearTimeout(window.urlValidationTimeout);
        window.urlValidationTimeout = setTimeout(() => {
            if (!value.trim()) {
                // ✅ Empty field
                setLocalErrors((prev) => ({ ...prev, url: 'Company website is required.' }));
            } else if (!validateURL(value.trim())) {
                // ✅ Invalid format
                setLocalErrors((prev) => ({ ...prev, url: 'Please enter a valid URL.' }));
            } else {
                // ✅ Valid input
                setLocalErrors((prev) => ({ ...prev, url: '' }));
                handleChange({ target: { name: 'url', value } });
            }
        }, 800);
    };

    // Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // Store previous values
    };

    // Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();

        // ✅ Ask parent to validate — returns false if any error
        if (typeof handleGeneralSubmit === 'function') {
            const result = await handleGeneralSubmit(e); // only submits if valid

            if (result === false) {
                setShowErrorMessage(true); // ❌ Show error if validation failed
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return; // Stop here
            }

            setShowErrorMessage(false); // ✅ Clear error if save was successful
            setIsEditing(false); // ✅ Exit edit mode after saving
            setForceRerenderKey(prev => prev + 1); // ✅ Force re-render
        } else {
            console.error('Error: handleGeneralSubmit is not a function.');
        }
    };


    // Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false);
        // Restore each field manually
        Object.entries(tempFormData).forEach(([key, value]) => {
            handleChange({ target: { name: key, value } });
        });
        handleResetLogo(); // ✅ Reset image
        setLogoPreview(logo); // ✅ Reset local preview
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleEditImage = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click(); // ✅ triggers file dialog
        }
    };

    const handleFileChange = (e) => {
        console.log('file change')
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
            handleChange({ target: { name: 'logo', value: file } });
        }
    };

    return (
        <>
            <div key={forceRerenderKey}>
                <Card className="card-premium">
                    <Card.Body>
                        {/* Title and Edit Button */}
                        <Row className="align-items-center3">
                            <Col>
                                {isEditPage && (
                                    <div className="float-end">
                                        {!isEditing ? (
                                            <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                        ) : (
                                            <>
                                                <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                                <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                            </>
                                        )}
                                    </div>
                                )}
                                <h3 className="text-start">1. General Info</h3>
                                <hr />
                            </Col>
                        </Row>
                        
                        {isEditing ? (
                            <Row className="mb-4">
                                {/* Company Logo */}
                                <Col md={2} className="upload-image-wrapper">
                                    <Form.Group controlId="companyLogo">
                                        {logoPreview?.trim() ? (
                                            <div
                                                key={logoPreview} // ✅ ensures re-render when logoPreview changes
                                                className={`upload-preview ${!isEditing ? 'not-editing' : 'editing'}`}
                                            >
                                                <Image
                                                    src={logoPreview}
                                                    alt="Profile Preview"
                                                    width="136"
                                                    height="136"
                                                    roundedCircle
                                                    className={`preview-image ${!isEditing ? 'not-editing' : 'editing'}`}
                                                />
                                                {isEditing && (
                                                    <>
                                                        <Button variant="light" className="btn-edit" onClick={handleEditImage}>
                                                            <img src={icImgEdit} alt="Edit" />
                                                        </Button>
                                                        <Button variant="light" className="btn-delete" onClick={handleDeleteImage}>
                                                            <img src={icDelete} alt="Delete" />
                                                        </Button>
                                                        <Form.Control
                                                            type="file"
                                                            accept="image/*"
                                                            name="logo"
                                                            onChange={handleFileChange}
                                                            className="upload-input"
                                                            hidden
                                                            ref={fileInputRef}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="upload-box" onClick={() => fileInputRef.current?.click()}> {/* ✅ Trigger input on click */}
                                                <img src={icImgPreview} alt="Upload Icon" className="upload-icon" />
                                                <div className="upload-text">Upload logo</div>
                                                <div className="upload-subtext">JPEG or PNG.</div>
                                                <Form.Control
                                                    type="file"
                                                    accept="image/*"
                                                    name="logo"
                                                    onChange={handleFileChange}
                                                    className="upload-input"
                                                    hidden
                                                    ref={fileInputRef}
                                                />
                                            </div>
                                        )}

                                    </Form.Group>
                                </Col>

                                {/* Form Fields */}
                                <Col md={10}>
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Group className="validated" controlId="title">
                                                <Form.Label>Company Name <span>*</span></Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="title"
                                                    value={formData.title}
                                                    onChange={handleTitleChange}
                                                    isInvalid={!!errors.title || !!localErrors.title}
                                                    required
                                                    disabled={!isEditing}
                                                />
                                                {/* ✅ Custom error icon + tooltip */}
                                                {(errors.title || localErrors.title) && (
                                                    <div className="custom-error-icon-wrapper">
                                                        <img
                                                            src={icValidationError}
                                                            alt={errors.title || localErrors.title}
                                                            className="custom-error-icon"
                                                        />
                                                        <div className="custom-tooltip">
                                                            {errors.title || localErrors.title}
                                                        </div>
                                                    </div>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group controlId="bestPlacetoWorkRating">
                                                <Form.Label>GPTW Rating</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    name="bestPlacetoWorkRating"
                                                    value={formData.bestPlacetoWorkRating}
                                                    onChange={(e) => {
                                                        const value = Math.min(100, Math.max(0, Number(e.target.value))); // ✅ Ensures value stays between 0 and 100
                                                        handleChange({ target: { name: 'bestPlacetoWorkRating', value } });
                                                    }}
                                                    min="0" // ✅ Minimum value set to 0
                                                    max="100" // ✅ Maximum value set to 100
                                                    disabled={!isEditing}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={3}>
                                            <Form.Group controlId="accessLevel">
                                                <Form.Label>Access Level</Form.Label>
                                                <Form.Control
                                                    as="select"
                                                    name="accessLevel"
                                                    value={formData.accessLevel}
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                >
                                                    <option value="" disabled>Please select</option>
                                                    <option value="Free">Free</option>
                                                    <option value="Custom">Custom</option>
                                                    <option value="Pro">Pro</option>
                                                    <option value="Premium">Premium</option>
                                                </Form.Control>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="validated" controlId="url">
                                                <Form.Label>Company Website <span>*</span></Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="url"
                                                    value={urlInput}
                                                    onChange={handleURLChange}
                                                    isInvalid={!!errors.url || !!localErrors.url}
                                                    required
                                                    disabled={!isEditing}
                                                />

                                                {/* ✅ Custom error icon + tooltip */}
                                                {(errors.url || localErrors.url) && (
                                                    <div className="custom-error-icon-wrapper">
                                                        <img src={icValidationError} alt={localErrors.url} className="custom-error-icon" />
                                                        <div className="custom-tooltip">
                                                            {localErrors.url}
                                                        </div>
                                                    </div>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group controlId="bestPlacetoWorkDescription">
                                                <Form.Label>GPTW Description</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="bestPlacetoWorkDescription"
                                                    value={formData.bestPlacetoWorkDescription}
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        ) : (
                            <>
                                <Row className="mb-4">
                                    <Col md={8} className="d-flex">
                                        <div className="upload-image-wrapper">
                                            <Form.Group controlId="companyLogo">
                                                <div className="upload-preview not-editing">
                                                    <Image
                                                        src={logo?.trim() ? logo : icImgPreview} // ✅ fallback if logo is empty
                                                        alt="Profile Preview"
                                                        roundedCircle
                                                        className="preview-image"
                                                    />
                                                </div>
                                            </Form.Group>
                                        </div>
                                        <div className="ms-3">
                                            <h1>{formData.title}</h1>
                                            <p>
                                                {urlInput && (
                                                    <a
                                                        href={urlInput.startsWith('http') ? urlInput : `https://${urlInput}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {urlInput}
                                                    </a>
                                                )}
                                                <span className="dot"></span>
                                                Access Level: &nbsp;<strong>{formData.accessLevel}</strong>
                                            </p>
                                            {isEditPage && (
                                                <>
                                                    <div className="status-wrapper">
                                                        <p>
                                                            <span className={
                                                                formData.accountApprovalStatus?.toLowerCase() === "approved" ? "approved" :
                                                                formData.accountApprovalStatus?.toLowerCase() === "rejected" ? "rejected" :
                                                                formData.accountApprovalStatus?.toLowerCase() === "lifecycle review" ? "lifecycle-review" :
                                                                formData.accountApprovalStatus?.toLowerCase() === "under review" ? "under-review" : ""
                                                            }>
                                                                <span className="d-flex align-items-center gap-2">
                                                                    {formData.accountApprovalStatus?.toLowerCase() === "approved" && <img src={icApproved} alt="Approved" />}
                                                                    {formData.accountApprovalStatus?.toLowerCase() === "rejected" && <img src={icRejected} alt="Rejected" />}
                                                                    {formData.accountApprovalStatus?.toLowerCase() === "lifecycle review" && <img src={icRLifecycleReview} alt="Lifecycle Review" />}
                                                                    {formData.accountApprovalStatus?.toLowerCase() === "under review" && <img src={icUnderReview} alt="Under Review" />}
                                                                    {formData.accountApprovalStatus}
                                                                </span>
                                                            </span>
                                                            <span 
                                                                className={`badge ${formData.activityStatus ? 'bg-active' : 'bg-inactive'}`}
                                                            >
                                                                {formData.activityStatus ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </p>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </Col>
                                    {/* Form Fields */}
                                    <Col md={4} className="d-flex justify-content-end align-items-center">
                                        {/* GPTW Badge (If Rating > 90) */}
                                        {parseFloat(formData.bestPlacetoWorkRating) > 89 && (() => {
                                            const rating = parseFloat(formData.bestPlacetoWorkRating || 0);
                                            const redDash = rating;
                                            const grayDash = 100 - rating;
                                            
                                            return (
                                                <div className="gptw-badge d-flex align-items-center">
                                                    <div className="gptw-logo">
                                                        <Image src={gptwBanner} alt="aBROADIFY" title="aBROADIFY" />
                                                    </div>

                                                    <div className="gptw-text-circle">
                                                        <div className="gptw-circle position-relative">
                                                            <svg width="80" height="80" viewBox="0 0 36 36">
                                                                {/* Red arc */}
                                                                <circle
                                                                    cx="18"
                                                                    cy="18"
                                                                    r="16"
                                                                    fill="none"
                                                                    stroke="#ec1c24"
                                                                    strokeWidth="4"
                                                                    strokeDasharray={`${redDash} 100`}
                                                                    strokeDashoffset="0"
                                                                    transform="rotate(-90 18 18)"
                                                                />

                                                                {/* Gray arc */}
                                                                <circle
                                                                    cx="18"
                                                                    cy="18"
                                                                    r="16"
                                                                    fill="none"
                                                                    stroke="#d0d3d4"
                                                                    strokeWidth="4"
                                                                    strokeDasharray={`${grayDash} 100`}
                                                                    strokeDashoffset={`-${redDash}`}
                                                                    transform="rotate(-90 18 18)"
                                                                    strokeLinecap="square"
                                                                />
                                                            </svg>

                                                            <div className="gptw-percentage position-absolute top-50 start-50 translate-middle fw-bold">
                                                                {`${Math.round(rating)}%`}
                                                            </div>
                                                        </div>

                                                        <div className="gptw-text">
                                                            <span className="fw-bold text-dark">
                                                                Employees say<br />
                                                                this is a great<br />
                                                                place to work
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                    </Col>
                                </Row>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </div>
        </>
    );
};

export default CompanyAddGeneral;
